import { Button } from '@src/components/ui/button';
import { Typography } from '@src/components/ui/typography';

import { ScopedReactPortal } from '@shared/providers/PortalContainerContext';
import {
    LayoutBuilderTypeFull,
    ServiceProcessButtonForServiceType,
} from '@shared/types/process';
import { scrollFormIntoView } from '@shared/util/scrollFormIntoView';

import { css } from '@emotion/css';
import { ShadcnButton, ShadcnForm } from '@shadcn-features/core';
import { cx } from 'class-variance-authority';
import { ArrowRight, Trash2Icon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface FlowTabProps {
    items: {
        key: string;
        label: string;
        data: LayoutBuilderTypeFull['children'][0];
        children: React.ReactNode;
    }[];
    changeItem?: (id: string) => void;
    deleteTab?: () => void;
    buttons?: ServiceProcessButtonForServiceType[];
    childrenBtnForFlowTab?: React.ReactNode;
    tabProps?: {
        className?: string;
        variant?: 'default' | 'ghost';
    };
}

const getFieldIdsFromTabItem = (
    block?: LayoutBuilderTypeFull['children'][0],
) => {
    const fieldIds: string[] = [];

    const collect = (node?: LayoutBuilderTypeFull['children'][0]) => {
        if (!node) return;

        if (node.type === 'Field') {
            fieldIds.push(node.options.processFieldId);
        }

        node.children?.forEach(collect);
    };

    collect(block);

    return fieldIds;
};

export const FlowTab: FC<FlowTabProps> = ({
    items,
    changeItem,
    deleteTab,
    childrenBtnForFlowTab,
    tabProps,
}) => {
    const { t } = useTranslation();

    const form = ShadcnForm.useFormInstance();

    const [selected, setSelected] = useState<string | null>(items?.[0]?.key);

    const topRef = useRef<HTMLDivElement | null>(null);
    const stepsRef = useRef<HTMLDivElement | null>(null);

    const [isCheckData, setIsCheckData] = useState(false);

    const setSelectedItem = useMemo(() => {
        return items.find((item) => item.key === selected);
    }, [items, selected]);

    const isLastSelected = useMemo(() => {
        return (
            items.findIndex((item) => item.key === selected) ===
            items.length - 1
        );
    }, [items, selected]);

    const isFirstSelected = useMemo(() => {
        return items.findIndex((item) => item.key === selected) === 0;
    }, [items, selected]);

    const selectedItemIndex = useMemo(() => {
        return items.findIndex((item) => item.key === selected);
    }, [items, selected]);

    const scrollActiveStepIntoView = (step: HTMLButtonElement | null) => {
        if (isCheckData || !step) return;

        const viewport = stepsRef.current;

        if (!viewport) return;

        const viewportBounds = viewport.getBoundingClientRect();
        const stepBounds = step.getBoundingClientRect();
        const offset =
            stepBounds.left < viewportBounds.left
                ? stepBounds.left - viewportBounds.left
                : Math.max(0, stepBounds.right - viewportBounds.right);

        if (offset !== 0) {
            viewport.scrollTo({ left: viewport.scrollLeft + offset });
        }
    };

    const isActiveStep = useCallback(
        (index: number) => {
            return index <= selectedItemIndex;
        },
        [selectedItemIndex],
    );

    const isCompletedStep = useCallback(
        (index: number) => {
            return index < selectedItemIndex;
        },
        [selectedItemIndex],
    );

    const handleScroll = useCallback(() => {
        requestAnimationFrame(() => scrollFormIntoView(topRef.current));
    }, []);

    useEffect(() => {
        if (items.length > 0 && selected) {
            const isHaveSelectedKay = items.some(
                (item) => item.key === selected,
            );
            if (!isHaveSelectedKay) {
                setSelected(items?.[0]?.key);
            }
        } else if (items.length === 0 && selected) {
            setSelected(null);
        } else if (items.length > 0 && !selected) {
            setSelected(items?.[0]?.key);
        }
    }, [items, selected]);

    const handelChangeItem = useCallback(
        (selected: string) => {
            setIsCheckData(false);
            changeItem?.(selected);
            setSelected(selected);
            handleScroll();
        },
        [changeItem, handleScroll],
    );

    const validateCurrentItem = useCallback(async () => {
        const fieldIds = getFieldIdsFromTabItem(setSelectedItem?.data);

        if (!fieldIds.length) {
            return true;
        }

        return await form.trigger(fieldIds, {
            shouldFocus: true,
        });
    }, [form, setSelectedItem]);

    const renderActions = (className?: string) => (
        <div
            data-scope-office
            className={cx('flex justify-between gap-2.5 sm:gap-5', className)}
        >
            {!isFirstSelected && !isCheckData && (
                <div
                    className={css`
                        flex: 1;
                    `}
                >
                    <Button
                        size={'lg'}
                        type='button'
                        variant='outlinePortal'
                        className={
                            'font-montserrat! max-sm:font-inter! h-[52px]! w-full rounded-xl! text-base! font-semibold! text-(--color-select-text)! max-sm:rounded-[14px]! max-sm:border-0 max-sm:bg-(--color-bg-grey-v2)! max-sm:text-sm! max-sm:leading-[1.4]! max-sm:font-medium! max-sm:tracking-[-0.14px] max-sm:text-(--color-muted-text)!'
                        }
                        onClick={() => {
                            setSelected((prev) => {
                                const currentIndex = items.findIndex(
                                    (item) => item.key === prev,
                                );
                                return items[currentIndex - 1]?.key;
                            });
                            handleScroll();
                        }}
                    >
                        {t('translation:back')}
                    </Button>
                </div>
            )}
            <div
                hidden={isCheckData}
                className={css`
                    flex: 1;
                `}
            >
                <Button
                    className={
                        'h-[52px]! w-full rounded-xl! max-sm:rounded-[14px]!'
                    }
                    size={'lg'}
                    type='button'
                    variant={'brandGreen'}
                    onClick={async () => {
                        const isValidCurrentItem = await validateCurrentItem();

                        if (!isValidCurrentItem) {
                            return;
                        }

                        if (isLastSelected) {
                            setIsCheckData(true);
                            handleScroll();
                            return;
                        }
                        setSelected((prev) => {
                            const currentIndex = items.findIndex(
                                (item) => item.key === prev,
                            );
                            return items[currentIndex + 1]?.key;
                        });
                        handleScroll();
                    }}
                >
                    <div
                        className={css`
                            display: flex;
                            align-items: center;
                            gap: 8px;
                        `}
                    >
                        <Typography.Text
                            className={
                                'font-montserrat! max-sm:font-inter! text-base! font-medium! max-sm:leading-normal!'
                            }
                        >
                            {t('translation:continue')}
                        </Typography.Text>
                        <ArrowRight
                            size={14}
                            className='hidden text-inherit sm:block'
                        />
                    </div>
                </Button>
            </div>

            <div
                className='flex w-full flex-col gap-2 bg-(--color-bg-v4)'
                hidden={!isCheckData}
            >
                {childrenBtnForFlowTab}
            </div>
        </div>
    );

    return (
        <div ref={topRef} className={cx('min-w-0', tabProps?.className)}>
            {/* LINE START */}
            <div
                hidden={isCheckData}
                className='mb-10 w-full border-b border-(--color-border) px-0 pb-[22px] lg:px-[19px]'
            >
                <div
                    ref={stepsRef}
                    className='office-scrollbar w-full overflow-x-auto overflow-y-hidden pb-2'
                >
                    <div className='flex w-full items-center'>
                        {items.map((item, index) => (
                            <Item
                                key={item.key}
                                index={index}
                                isActiveStep={isActiveStep}
                                isCompletedStep={isCompletedStep}
                                item={item}
                                activeStepRef={
                                    index === selectedItemIndex
                                        ? scrollActiveStepIntoView
                                        : undefined
                                }
                            />
                        ))}

                        <button
                            type='button'
                            className='font-montserrat! flex h-8 w-8 shrink-0 cursor-auto! items-center justify-center rounded-full border-0 bg-(--color-bg-v5) text-base font-semibold text-(--color-text-v1)'
                        >
                            {items.length + 1}
                        </button>
                    </div>
                </div>
            </div>
            {/* LINE END */}

            {/* BODY START */}
            <div
                className={cx(css`
                    display: flex;
                    flex-direction: column;
                `)}
            >
                {!isCheckData && (
                    <ShowCurrentStepChild
                        changeItem={handelChangeItem}
                        deleteTab={deleteTab}
                        items={items}
                        selected={selected}
                        selectedItemIndex={selectedItemIndex}
                        setSelectedItem={setSelectedItem}
                    />
                )}

                {isCheckData && (
                    <ShowPageCheckData
                        changeItem={handelChangeItem}
                        deleteTab={deleteTab}
                        items={items}
                        selected={selected}
                        selectedItemIndex={selectedItemIndex}
                        setSelectedItem={setSelectedItem}
                    />
                )}

                {/* ACTIONS START */}
                {selected &&
                items.length > 0 &&
                tabProps?.variant !== 'ghost' ? (
                    <ScopedReactPortal>
                        {renderActions(
                            'fixed inset-x-0 bottom-0 z-1 flex-row bg-(--color-bg-v4) px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:hidden',
                        )}
                    </ScopedReactPortal>
                ) : null}
                {selected && items.length > 0 && (
                    <>
                        {renderActions(
                            tabProps?.variant === 'ghost'
                                ? 'mt-6 flex-row sm:flex-col-reverse'
                                : 'mt-6 hidden sm:flex sm:flex-row sm:bg-transparent sm:px-0 sm:py-0',
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

function Item({
    item,
    index,
    isActiveStep,
    isCompletedStep,
    setSelected,
    activeStepRef,
}: {
    item: FlowTabProps['items'][0];
    index: number;
    isActiveStep?: (index: number) => boolean;
    isCompletedStep?: (index: number) => boolean;
    setSelected?: (key: string) => void;
    activeStepRef?: React.RefCallback<HTMLButtonElement>;
}) {
    return (
        <div
            key={item.key}
            className='flex min-w-[min(76px,calc((100%-2rem)/4))] flex-1 items-center'
        >
            <button
                ref={activeStepRef}
                type='button'
                className={cx(
                    'font-montserrat! flex h-8 w-8 shrink-0 cursor-auto! items-center justify-center rounded-full border-0 text-base font-semibold transition-colors',
                    isActiveStep?.(index)
                        ? 'bg-(--color-brand-green) text-white'
                        : 'bg-(--color-bg-v5) text-(--color-text-v1)',
                )}
                onClick={() => setSelected?.(item.key)}
            >
                {index + 1}
            </button>
            <div className='min-w-0 flex-1'>
                <div
                    className={cx(
                        'mx-[min(18px,25%)] h-1.5 rounded-[27.862px] transition-colors',
                        isCompletedStep?.(index)
                            ? 'bg-(--color-brand-green)'
                            : 'bg-(--color-bg-v5)',
                    )}
                />
            </div>
        </div>
    );
}

function ShowCurrentStepChild({
    selected,
    deleteTab,
    changeItem,
    setSelectedItem,
    selectedItemIndex,
    items,
    isReadonly,
}: {
    selected: string | null;
    deleteTab?: () => void;
    changeItem?: (id: string) => void;
    setSelectedItem?: FlowTabProps['items'][0];
    selectedItemIndex: number;
    items: FlowTabProps['items'];
    isReadonly?: boolean;
}) {
    const { t } = useTranslation();

    if (isReadonly) {
        return (
            <div className='flex flex-col'>
                <div className='flex w-full flex-1 items-center justify-between'>
                    <span
                        className={cx(
                            'text-base md:text-[18px] font-bold text-(--color-card-text) lg:font-montserrat font-inter',
                        )}
                    >
                        {setSelectedItem?.label}
                    </span>
                    <ShadcnButton
                        className='w-fit min-w-30 justify-end p-0! text-sm! font-medium!'
                        variant={'greenText'}
                        onClick={() => {
                            if (!setSelectedItem?.key) return;
                            changeItem?.(setSelectedItem?.key);
                        }}
                    >
                        {t('translation:edit')}
                    </ShadcnButton>
                </div>
                {setSelectedItem?.children && (
                    <div className='mt-5 lg:mt-6'>
                        {React.isValidElement(setSelectedItem.children)
                            ? React.cloneElement(setSelectedItem.children, {
                                  isReadOnly: true,
                                  classNameWrapper: 'gap-5',
                              })
                            : setSelectedItem.children}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='w-full'>
            <div
                hidden={isReadonly}
                className={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 9px;
                `}
            >
                {selected && (
                    <span
                        className={cx(
                            css`
                                color: var(--color-brand-green);
                            `,
                            'font-montserrat text-sm font-medium',
                        )}
                    >
                        {t('translation:step')} {selectedItemIndex + 1}
                    </span>
                )}
                {!selected && items.length === 0 && (
                    <ShadcnButton
                        className='text-[var(--color-danger)]!'
                        icon={<Trash2Icon size={14} />}
                        variant='icon'
                        popconfirm={{
                            title: t('translation:areYouSure'),
                            onConfirm: () => {
                                deleteTab?.();
                            },
                        }}
                    />
                )}
            </div>
            <span
                hidden={isReadonly}
                className={
                    'font-montserrat text-xl font-bold text-(--color-card-text)'
                }
            >
                {setSelectedItem?.label}
            </span>

            {setSelectedItem && (
                <div className='mt-[30px]'>{setSelectedItem.children}</div>
            )}
        </div>
    );
}

function ShowPageCheckData({
    ...props
}: Parameters<typeof ShowCurrentStepChild>[0]) {
    const { t } = useTranslation();

    return (
        <div className='flex flex-col gap-6 lg:gap-3'>
            <div>
                <h4 className='font-montserrat mb-2 block text-base font-bold text-(--color-select-text) lg:mb-7 lg:text-xl'>
                    {t('translation:checkData')}
                </h4>
                <h5 className='font-inter! text-[14px] font-medium text-(--color-text-grey)'>
                    {t('translation:checkDataMessage')}
                </h5>
            </div>

            <div className='flex w-full flex-col gap-3'>
                {props?.items?.map((item) => (
                    <div
                        key={item.key}
                        className='w-full rounded-xl bg-(--color-bg-v6) p-4 lg:p-5'
                    >
                        <ShowCurrentStepChild
                            {...props}
                            key={item.key}
                            isReadonly
                            changeItem={props.changeItem}
                            setSelectedItem={item}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
